export const TARGET_RATIO = {
    horizontal: 16 / 9,
    vertical: 9 / 16,
};

export const OUTPUT_SIZE = {
    horizontal: { width: 1920, height: 1080 },
    vertical: { width: 1080, height: 1920 },
};

export const MAX_CREATIVE_BYTES = 3145728;
export const MAX_CREATIVE_VIDEO_BYTES = 80 * 1024 * 1024;
export const MAX_CREATIVE_VIDEO_TRANSCODE_THRESHOLD_BYTES = 12 * 1024 * 1024;

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'webm']);
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);

export function getFileExtension(fileName) {
    const parts = String(fileName || '').split('.');
    if (parts.length < 2) {
        return '';
    }
    return parts[parts.length - 1].toLowerCase();
}

export function isVideoFile(file) {
    const ext = getFileExtension(file?.name);
    if (VIDEO_EXTENSIONS.has(ext)) {
        return true;
    }
    return Boolean(file?.type && file.type.startsWith('video/'));
}

export function isCreativeVideoUrl(url) {
    if (!url || typeof url !== 'string') {
        return false;
    }
    if (url.startsWith('data:video/')) {
        return true;
    }
    const ext = getFileExtension(url.split('?')[0]);
    return VIDEO_EXTENSIONS.has(ext) || ext === 'm4v' || ext === 'mkv';
}

export function creativeVideoMimeTypeFromUrl(url) {
    if (!url || typeof url !== 'string') {
        return 'video/mp4';
    }
    if (url.startsWith('data:video/')) {
        const mime = url.slice(5).split(';')[0].trim().toLowerCase();
        return mime || 'video/mp4';
    }
    const ext = getFileExtension(url.split('?')[0]);
    if (ext === 'mov') {
        return 'video/quicktime';
    }
    if (ext === 'webm') {
        return 'video/webm';
    }
    if (ext === 'm4v' || ext === 'mkv' || ext === 'mp4') {
        return 'video/mp4';
    }
    return 'video/mp4';
}

export function guessCreativeMediaKindFromUrl(url) {
    return isCreativeVideoUrl(url) ? 'video' : 'image';
}

export function creativeSlotLabel(slot) {
    return slot === 'horizontal' ? 'Horizontal (16:9)' : 'Vertical (9:16)';
}

export function creativeMediaKindLabel(kind) {
    return kind === 'video' ? 'Video' : 'Image';
}

export function isImageFile(file) {
    const ext = getFileExtension(file?.name);
    if (IMAGE_EXTENSIONS.has(ext)) {
        return true;
    }
    return Boolean(file?.type && file.type.startsWith('image/'));
}

export function isAllowedCreativeFile(file) {
    return isImageFile(file) || isVideoFile(file);
}

export function maxBytesForCreativeFile(file) {
    return isVideoFile(file) ? MAX_CREATIVE_VIDEO_BYTES : MAX_CREATIVE_BYTES;
}

export function creativeVideoHint(slot) {
    const aspect = slot === 'horizontal' ? '16:9' : '9:16';
    return `Videos: must already be ${aspect} and up to 80MB (large videos are re-encoded).`;
}

export function isTargetAspectRatio(width, height, slot, tolerance = 0.02) {
    if (!width || !height) {
        return false;
    }
    const ratio = width / height;
    const target = TARGET_RATIO[slot];
    return Math.abs(ratio - target) <= tolerance;
}

export function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}

export function loadImageFromDataUrl(dataUrl) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = dataUrl;
    });
}

export function loadVideoDimensions(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
            const width = video.videoWidth;
            const height = video.videoHeight;
            URL.revokeObjectURL(url);
            resolve({ width, height });
        };
        video.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Could not read video metadata'));
        };
        video.src = url;
    });
}

function createImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });
}

function getRadianAngle(degreeValue) {
    return (degreeValue * Math.PI) / 180;
}

function rotateSize(width, height, rotation) {
    const rotRad = getRadianAngle(rotation);
    return {
        width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
        height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
}

/**
 * Crop and optionally rotate an image; returns a PNG blob at outputSize.
 */
export async function getCroppedImageBlob(imageSrc, pixelCrop, rotation = 0, outputSize) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas not supported');
    }

    const rotRad = getRadianAngle(rotation);
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);

    const croppedCanvas = document.createElement('canvas');
    const croppedCtx = croppedCanvas.getContext('2d');
    if (!croppedCtx) {
        throw new Error('Canvas not supported');
    }

    croppedCanvas.width = outputSize.width;
    croppedCanvas.height = outputSize.height;

    croppedCtx.drawImage(
        canvas,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        outputSize.width,
        outputSize.height
    );

    return new Promise((resolve, reject) => {
        croppedCanvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Failed to export cropped image'));
                return;
            }
            resolve(blob);
        }, 'image/png');
    });
}

/**
 * Resize image to exact output dimensions using cover fit (for already-correct aspect images).
 */
export async function resizeImageToOutputBlob(imageSrc, outputSize) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas not supported');
    }

    const targetAspect = outputSize.width / outputSize.height;
    const sourceAspect = image.width / image.height;

    let sx = 0;
    let sy = 0;
    let sw = image.width;
    let sh = image.height;

    if (sourceAspect > targetAspect) {
        sw = image.height * targetAspect;
        sx = (image.width - sw) / 2;
    } else if (sourceAspect < targetAspect) {
        sh = image.width / targetAspect;
        sy = (image.height - sh) / 2;
    }

    canvas.width = outputSize.width;
    canvas.height = outputSize.height;
    ctx.drawImage(image, sx, sy, sw, sh, 0, 0, outputSize.width, outputSize.height);

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Failed to export image'));
                return;
            }
            resolve(blob);
        }, 'image/png');
    });
}

function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
}

export function blobToRawBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(arrayBufferToBase64(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });
}

export function fileToRawBase64(file) {
    return blobToRawBase64(file);
}

export function blobToPreviewUrl(blob) {
    return URL.createObjectURL(blob);
}

function canvasToBlob(image, width, height, mimeType, quality) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas not supported');
    }
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(image, 0, 0, width, height);
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Failed to encode image'));
                    return;
                }
                resolve(blob);
            },
            mimeType,
            mimeType === 'image/jpeg' ? quality : undefined
        );
    });
}

/**
 * Re-encode an image (from data URL) until blob size is at or below maxBytes.
 */
export async function downscaleImageDataUrlToMaxBytes(dataUrl, maxBytes = MAX_CREATIVE_BYTES) {
    const image = await createImage(dataUrl);
    if (!image.width || !image.height) {
        throw new Error('Invalid image dimensions');
    }

    let width = image.width;
    let height = image.height;
    let quality = 0.92;
    let blob = await canvasToBlob(image, width, height, 'image/jpeg', quality);

    for (let attempt = 0; attempt < 24 && blob.size > maxBytes; attempt += 1) {
        if (quality > 0.45) {
            quality = Math.max(0.45, quality - 0.07);
        } else {
            width = Math.max(320, Math.floor(width * 0.85));
            height = Math.max(320, Math.floor(height * 0.85));
            quality = 0.82;
        }
        blob = await canvasToBlob(image, width, height, 'image/jpeg', quality);
    }

    if (blob.size > maxBytes) {
        throw new Error('Could not reduce image below 3MB');
    }

    return blob;
}

/**
 * If an image file exceeds maxBytes, downscale and return a new File + data URL.
 */
export async function prepareImageFileForUpload(file, maxBytes = MAX_CREATIVE_BYTES) {
    const dataUrl = await readFileAsDataUrl(file);
    if (file.size <= maxBytes) {
        return { file, dataUrl };
    }

    const blob = await downscaleImageDataUrlToMaxBytes(dataUrl, maxBytes);
    const baseName = String(file.name || 'image').replace(/\.[^.]+$/, '');
    const newFile = new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
    const newDataUrl = await readFileAsDataUrl(newFile);
    return { file: newFile, dataUrl: newDataUrl };
}

/**
 * Ensure a blob (e.g. cropped PNG) is within maxBytes by re-encoding if needed.
 */
export async function ensureBlobUnderMaxBytes(blob, maxBytes = MAX_CREATIVE_BYTES) {
    if (blob.size <= maxBytes) {
        return blob;
    }
    const dataUrl = await readFileAsDataUrl(blob);
    return downscaleImageDataUrlToMaxBytes(dataUrl, maxBytes);
}
